import { useEffect, useMemo, useRef, useState } from 'react';
import {
  getAgentPersonaFiles,
  putAgentPersonaFiles,
} from '../../../../lib/backend/world';
import {
  mono,
  dmMono,
  labelStyle,
  secondaryButtonStyle,
  primaryButtonStyle,
} from './tokens';

const FILE_TABS = [
  { key: 'identity_md', label: 'identity.md' },
  { key: 'soul_md', label: 'soul.md' },
  { key: 'user_md', label: 'user.md' },
  { key: 'agents_md', label: 'AGENTS.md' },
];

const AUTO_SOURCE = 'openclaw_identity_files';
const MANUAL_SOURCE = 'manual';

function emptyFileMap() {
  return { identity_md: '', soul_md: '', user_md: '', agents_md: '' };
}

function normalizeFiles(files) {
  const next = emptyFileMap();
  if (!files) return next;
  for (const key of Object.keys(next)) {
    const value = files[key];
    next[key] = typeof value === 'string' ? value : '';
  }
  return next;
}

const DEFAULT_FILENAMES = {
  identity_md: 'identity.md',
  soul_md: 'soul.md',
  user_md: 'user.md',
  agents_md: 'AGENTS.md',
};

export default function PersonaFilesEditor({
  agentId,
  active,
  color = '#53b1fd',
  onCharacterConfigRegenerated,
  seededFiles = null,
}) {
  const [activeTab, setActiveTab] = useState(FILE_TABS[0].key);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [serverState, setServerState] = useState(null);
  const [draft, setDraft] = useState(emptyFileMap);
  const [notice, setNotice] = useState(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (!active || !agentId) return undefined;
    setLoadError(null);
    setSaveError(null);
    setNotice(null);

    if (seededFiles) {
      const seededState = {
        files: normalizeFiles(seededFiles),
        filenames: { ...DEFAULT_FILENAMES },
        files_hash: 'seed',
        character_config_source: 'seed',
        present: true,
      };
      setServerState(seededState);
      setDraft(normalizeFiles(seededFiles));
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    getAgentPersonaFiles(agentId)
      .then((data) => {
        if (cancelled) return;
        setServerState(data);
        setDraft(normalizeFiles(data.files));
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(
          err instanceof Error && err.message
            ? err.message
            : 'Failed to load persona files.',
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [active, agentId, seededFiles]);

  const serverFiles = useMemo(() => normalizeFiles(serverState?.files), [serverState]);
  const dirty = useMemo(() => {
    return FILE_TABS.some((tab) => (draft[tab.key] || '') !== (serverFiles[tab.key] || ''));
  }, [draft, serverFiles]);

  const source = serverState?.character_config_source;
  const isSeedMode = Boolean(seededFiles);
  const sourceLabel =
    isSeedMode ? 'Local seed · changes not persisted' :
    source === AUTO_SOURCE ? 'Auto · synced from files' :
    source === MANUAL_SOURCE ? 'Manual · character edits locked' :
    source ? source : 'Unknown';
  const sourceColor =
    isSeedMode ? '#a78bfa' :
    source === AUTO_SOURCE ? '#4ade80' :
    source === MANUAL_SOURCE ? '#fbbf24' : 'rgba(255,255,255,0.45)';

  const handleChange = (key, value) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
    setSaveError(null);
    setNotice(null);
  };

  const handleSave = async () => {
    if (!agentId || saving) return;
    setSaving(true);
    setSaveError(null);
    setNotice(null);
    const changed = {};
    for (const tab of FILE_TABS) {
      if ((draft[tab.key] || '') !== (serverFiles[tab.key] || '')) {
        changed[tab.key] = draft[tab.key] || '';
      }
    }
    if (Object.keys(changed).length === 0) {
      setSaving(false);
      return;
    }
    if (isSeedMode) {
      setServerState((prev) => ({
        ...(prev || {}),
        files: normalizeFiles(draft),
      }));
      setNotice('Local seed updated (this agent is not wired to a backend, so nothing was sent).');
      setSaving(false);
      return;
    }
    try {
      const result = await putAgentPersonaFiles(agentId, changed);
      setServerState(result);
      setDraft(normalizeFiles(result.files));
      if (result.regenerated && result.character_config) {
        onCharacterConfigRegenerated?.(result.character_config);
        setNotice('Character config regenerated from the updated files.');
      } else if (result.character_config_source === MANUAL_SOURCE) {
        setNotice(
          'Files saved. Character config is set to manual, so it was not regenerated.',
        );
      } else {
        setNotice('Files saved.');
      }
    } catch (err) {
      const message =
        err instanceof Error && err.message
          ? err.message
          : 'Failed to save persona files.';
      setSaveError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleRevert = () => {
    setDraft(normalizeFiles(serverState?.files));
    setSaveError(null);
    setNotice(null);
  };

  const activeTabConfig = FILE_TABS.find((t) => t.key === activeTab) || FILE_TABS[0];

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [draft, activeTab, loading]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'grid', gap: 3 }}>
          <div
            style={{
              ...mono,
              fontSize: 11,
              color: 'rgba(255,255,255,0.45)',
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
            }}
          >
            Persona Files
          </div>
          <div
            style={{
              ...dmMono,
              fontSize: 9,
              color: sourceColor,
              letterSpacing: '0.05em',
            }}
          >
            {sourceLabel}
          </div>
        </div>
        <div
          style={{
            ...mono,
            fontSize: 9,
            color: 'rgba(255,255,255,0.4)',
            maxWidth: 360,
            lineHeight: 1.45,
            textAlign: 'right',
          }}
        >
          {isSeedMode
            ? 'Preview of seeded identity files. Edits are local to this tab.'
            : source === AUTO_SOURCE
              ? 'Saving will re-synthesize the character config from these files.'
              : source === MANUAL_SOURCE
                ? 'Character config is manual — file edits are saved verbatim.'
                : 'Edit the raw identity files that shape this character.'}
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          gap: 2,
        }}
      >
        {FILE_TABS.map((tab) => {
          const isActive = tab.key === activeTab;
          const tabDirty =
            (draft[tab.key] || '') !== (serverFiles[tab.key] || '');
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                ...dmMono,
                fontSize: 10,
                background: 'none',
                border: 'none',
                padding: '8px 14px',
                color: isActive ? 'white' : 'rgba(255,255,255,0.4)',
                borderBottom: isActive
                  ? `2px solid ${color}`
                  : '2px solid transparent',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              {tab.label}
              {tabDirty ? (
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    background: color,
                    display: 'inline-block',
                  }}
                />
              ) : null}
            </button>
          );
        })}
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
        }}
      >
        {loading ? (
          <div
            style={{
              ...mono,
              fontSize: 11,
              color: 'rgba(255,255,255,0.5)',
              padding: 12,
            }}
          >
            Loading persona files...
          </div>
        ) : loadError ? (
          <div
            style={{
              ...mono,
              fontSize: 11,
              color: '#fca5a5',
              padding: 12,
              border: '1px solid rgba(248,113,113,0.3)',
              borderRadius: 6,
              background: 'rgba(248,113,113,0.08)',
              whiteSpace: 'pre-wrap',
            }}
          >
            {loadError}
          </div>
        ) : (
          <>
            <div
              style={{
                ...labelStyle,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <span>
                Editing: {serverState?.filenames?.[activeTabConfig.key] || activeTabConfig.label}
              </span>
              <span style={{ color: 'rgba(255,255,255,0.3)' }}>
                {(draft[activeTabConfig.key] || '').length} chars
              </span>
            </div>
            <textarea
              ref={textareaRef}
              value={draft[activeTabConfig.key] || ''}
              onChange={(e) => handleChange(activeTabConfig.key, e.target.value)}
              spellCheck={false}
              placeholder={`No ${activeTabConfig.label} content yet. Type here to create it.`}
              style={{
                minHeight: 200,
                width: '100%',
                boxSizing: 'border-box',
                background: '#06080d',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 6,
                color: 'rgba(255,255,255,0.9)',
                padding: '12px 14px',
                ...dmMono,
                fontSize: 12,
                lineHeight: 1.55,
                resize: 'none',
                overflow: 'hidden',
                outline: 'none',
              }}
            />
          </>
        )}
      </div>

      {(saveError || notice) && !loading ? (
        <div
          style={{
            ...mono,
            fontSize: 10,
            color: saveError ? '#fca5a5' : '#86efac',
            whiteSpace: 'pre-wrap',
          }}
        >
          {saveError || notice}
        </div>
      ) : null}

      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <button
          onClick={handleRevert}
          disabled={!dirty || saving}
          style={{
            ...secondaryButtonStyle,
            opacity: !dirty || saving ? 0.4 : 1,
            cursor: !dirty || saving ? 'default' : 'pointer',
          }}
        >
          Revert
        </button>
        <button
          onClick={handleSave}
          disabled={!dirty || saving}
          style={{
            ...primaryButtonStyle,
            minWidth: 120,
            opacity: !dirty || saving ? 0.5 : 1,
            cursor: !dirty ? 'default' : saving ? 'progress' : 'pointer',
          }}
        >
          {saving ? 'Saving...' : 'Save files'}
        </button>
      </div>
    </div>
  );
}
